/**
 * AdminRegionalPanel — persistent corpus of regional-language pairs.
 *
 * Visible to super-admin + managers. Explains that the corpus survives
 * even if the group is removed from users.
 *
 * v50 — Adds:
 *  - Corrections count (in addition to raw pair count)
 *  - Per-language breakdown of translations & corrections
 *  - Manual "add pair" form for admin to seed the corpus directly
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, Loader2, Download, RefreshCw, Languages, Edit3, Plus } from 'lucide-react';
import { useAdminLang } from '../hooks/useAdminLang';

const REGIONAL_LANGS = [
  { id: 'djuka', label: 'Ndjuka' },
  { id: 'saramaka', label: 'Saramaka' },
  { id: 'creole_gy', label: 'Créole guyanais' },
  { id: 'hmong', label: 'Hmong' },
  { id: 'kalina', label: "Kali'na" },
  { id: 'palikur', label: 'Palikur' },
  { id: 'teko', label: 'Teko' },
  { id: 'wayana', label: 'Wayana' },
  { id: 'wayampi', label: 'Wayampi' },
];

const AdminRegionalPanel = ({ region = 'guyane' }) => {
  const isAfrica = region === 'africa';
  const [africaLangs, setAfricaLangs] = useState([]);
  useEffect(() => { if (isAfrica) axios.get('/api/gpt-africa/info', { params: { lang: 'fr' } }).then(r => setAfricaLangs(r.data?.languages || [])).catch(() => {}); }, [isAfrica]);
  const { at } = useAdminLang();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    source_lang: 'fr',
    source_text: '',
    target_lang: region === 'africa' ? 'zu' : 'djuka',
    target_text: '',
    is_correction: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/regional/stats', { params: { region } });
      setStats(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    fetch(`/api/admin/regional/export?region=${region}`, { credentials: 'include' })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'regional_corpus.csv';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Erreur export'));
  };

  const submitPair = async (e) => {
    e.preventDefault();
    if (!form.source_text.trim() || !form.target_text.trim()) {
      toast.error(at('regionalFormMissing') || 'Remplis les deux champs');
      return;
    }
    setSaving(true);
    try {
      await axios.post('/api/admin/regional/pairs', form);
      toast.success(at('regionalFormOk') || 'Paire ajoutée au corpus');
      setForm({ ...form, source_text: '', target_text: '', is_correction: false });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-regional-panel">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-fuchsia-500/10 border border-emerald-400/30 p-4">
        <div className="flex items-start gap-3">
          <Languages className="w-6 h-6 text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <div className="font-bold text-white mb-1">{at('regionalTitle')}</div>
            <p className="text-white/70">{at('regionalSubtitle')}</p>
          </div>
          <button onClick={load} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60" data-testid="regional-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          {/* Summary tiles: translations + corrections */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-fuchsia-300 mb-1">
                <Database className="w-4 h-4" />
                <span className="text-[10px] uppercase tracking-wider text-white/60">
                  {at('regionalCount') || 'paires apprises'}
                </span>
              </div>
              <div className="text-3xl font-bold text-white" data-testid="regional-total-pairs">
                {(stats?.total || 0).toLocaleString()}
              </div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-amber-300 mb-1">
                <Edit3 className="w-4 h-4" />
                <span className="text-[10px] uppercase tracking-wider text-white/60">
                  {at('regionalCorrections') || 'corrections'}
                </span>
              </div>
              <div className="text-3xl font-bold text-white" data-testid="regional-total-corrections">
                {(stats?.total_corrections || 0).toLocaleString()}
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-white/80">
                {at('regionalPerLang') || 'Par langue'}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddForm(v => !v)}
                  className="px-3 py-1.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-xs font-semibold flex items-center gap-1.5"
                  data-testid="regional-add-toggle-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> {at('regionalAdd') || 'Ajouter'}
                </button>
                <button
                  onClick={exportCsv}
                  className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1.5"
                  data-testid="regional-export-btn"
                >
                  <Download className="w-3.5 h-3.5" /> {at('regionalExport')}
                </button>
              </div>
            </div>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="regional-per-lang">
              {(stats?.per_lang || []).map(x => (
                <li key={x.lang} className="rounded-lg bg-black/20 border border-white/10 p-3 text-center">
                  <div className="text-[10px] uppercase text-white/50 tracking-wider">{x.lang}</div>
                  <div className="text-2xl font-bold text-fuchsia-300">{(x.count || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-amber-300 mt-0.5">
                    {(x.corrections || 0).toLocaleString()} {at('regionalCorrectionsShort') || 'corr.'}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Manual add form */}
          {showAddForm && (
            <form
              onSubmit={submitPair}
              className="rounded-2xl bg-white/5 border border-fuchsia-400/30 p-4 space-y-3"
              data-testid="regional-add-form"
            >
              <div className="text-sm font-semibold text-white/90">
                {at('regionalAddTitle') || 'Ajouter une traduction'}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={form.source_lang}
                  onChange={e => setForm({ ...form, source_lang: e.target.value })}
                  className="bg-white/10 border border-white/15 rounded-lg px-2 py-2 text-sm"
                  data-testid="regional-form-source-lang"
                >
                  <option value="fr" className="bg-gray-900">FR (français)</option>
                  <option value="en" className="bg-gray-900">EN (English)</option>
                  <option value="nl" className="bg-gray-900">NL (Nederlands)</option>
                </select>
                <select
                  value={form.target_lang}
                  onChange={e => setForm({ ...form, target_lang: e.target.value })}
                  className="bg-white/10 border border-white/15 rounded-lg px-2 py-2 text-sm"
                  data-testid="regional-form-target-lang"
                >
                  {(isAfrica ? africaLangs : REGIONAL_LANGS).map(l => (
                    <option key={l.id} value={l.id} className="bg-gray-900">{l.label}</option>
                  ))}
                </select>
              </div>
              <textarea
                value={form.source_text}
                onChange={e => setForm({ ...form, source_text: e.target.value })}
                placeholder={at('regionalFormSrcPh') || 'Phrase source (fr/en/nl)'}
                rows={2}
                className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-2 text-sm resize-none"
                data-testid="regional-form-source"
              />
              <textarea
                value={form.target_text}
                onChange={e => setForm({ ...form, target_text: e.target.value })}
                placeholder={at('regionalFormTgtPh') || 'Traduction dans la langue régionale'}
                rows={2}
                className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-2 text-sm resize-none"
                data-testid="regional-form-target"
              />
              <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_correction}
                  onChange={e => setForm({ ...form, is_correction: e.target.checked })}
                  data-testid="regional-form-is-correction"
                />
                {at('regionalFormCorrection') || 'Marquer comme correction (remplace la traduction précédente)'}
              </label>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-3 py-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-50 text-white text-sm font-semibold"
                  data-testid="regional-form-submit"
                >
                  {saving ? (at('regionalFormSaving') || 'Enregistrement…') : (at('regionalFormSave') || 'Enregistrer')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm"
                  data-testid="regional-form-cancel"
                >
                  {at('cancel') || 'Annuler'}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
};

export default AdminRegionalPanel;
